import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StickyNote, Save } from 'lucide-react';
import type { Chapter } from '../types';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalTitle,
  ResponsiveModalFooter,
} from './ui/responsive-modal';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';

interface ChapterNotesModalProps {
  chapter: Chapter | null | undefined;
  onSave: (notes: string) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ChapterNotesModal({ chapter, onSave, open, onOpenChange }: ChapterNotesModalProps) {
  const { t } = useTranslation();
  const [notes, setNotes] = useState(chapter?.notes || '');

  if (!chapter) return null;

  const handleSave = () => {
    onSave(notes);
    onOpenChange(false);
  };

  const suggestions = [
    t('notes.characterIntro', 'Character introduction'),
    t('notes.plotPoint', 'Major plot point'),
    t('notes.foreshadowing', 'Foreshadowing'),
    t('notes.conflict', 'Conflict escalation'),
    t('notes.resolution', 'Resolution'),
    t('notes.research', 'Needs research'),
    t('notes.revision', 'Needs revision'),
  ];

  const addSuggestion = (suggestion: string) => {
    const prefix = notes ? `${notes}\n• ` : '• ';
    setNotes(`${prefix}${suggestion}`);
  };

  return (
    <ResponsiveModal open={open} onOpenChange={onOpenChange}>
      <ResponsiveModalContent className="sm:max-w-lg">
        <ResponsiveModalHeader>
          <ResponsiveModalTitle className="flex items-center gap-2">
            <StickyNote size={20} className="text-amber-500" />
            {t('notes.chapterNotes', 'Chapter Notes')}
          </ResponsiveModalTitle>
        </ResponsiveModalHeader>

        <div className="space-y-4">
          {/* Chapter Info */}
          <div className="rounded-lg bg-muted p-3">
            <p className="text-sm text-muted-foreground">
              <span className="font-medium text-foreground">{chapter.title}</span>
              <span className="ml-2">
                ({chapter.wordCount.toLocaleString()} {t('projects.words')})
              </span>
            </p>
          </div>

          {/* Notes Textarea */}
          <div className="space-y-1.5">
            <Label htmlFor="chapter-notes">{t('notes.yourNotes', 'Your Notes')}</Label>
            <Textarea
              id="chapter-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={8}
              placeholder={t('notes.placeholder', 'Add notes about this chapter, plot points, character arcs, or reminders for editing...')}
            />
          </div>

          {/* Quick Suggestions */}
          <div>
            <p className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
              {t('notes.quickAdd', 'Quick Add')}
            </p>
            <div className="flex flex-wrap gap-2">
              {suggestions.map((suggestion) => (
                <button
                  key={suggestion}
                  onClick={() => addSuggestion(suggestion)}
                  className="rounded-full bg-amber-500/15 px-2.5 py-1 text-xs font-medium text-amber-700 transition-colors hover:bg-amber-500/25 dark:text-amber-400"
                >
                  + {suggestion}
                </button>
              ))}
            </div>
          </div>

          {/* Tips */}
          <div className="rounded-lg border border-border bg-muted/50 p-3 text-sm">
            <p className="mb-1 font-medium text-foreground">{t('notes.tips', 'Tips')}</p>
            <ul className="space-y-1 text-xs text-muted-foreground">
              <li>• {t('notes.tip1', 'Use notes to track plot threads and character arcs')}</li>
              <li>• {t('notes.tip2', 'Mark sections that need research or fact-checking')}</li>
              <li>• {t('notes.tip3', "Notes are private and won't appear in exports")}</li>
            </ul>
          </div>
        </div>

        <ResponsiveModalFooter className="sm:items-center sm:justify-between">
          <span className="text-xs text-muted-foreground">
            {notes.length} {t('notes.characters', 'characters')}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleSave}>
              <Save size={18} />
              {t('common.save')}
            </Button>
          </div>
        </ResponsiveModalFooter>
      </ResponsiveModalContent>
    </ResponsiveModal>
  );
}
